import type { Transaction } from "@rocicorp/zero";
import type { ConnectedAppUsageType } from "../db/schema";
import { type Schema, zql } from "./generatedDrizzleSchema";
import {
  commitStepUsages,
  liveConnectorIdsBySpace,
  liveConnectorUsageRollupsBySpaceQuery,
  liveUsagesOfConnectorQuery,
} from "./liveConnectorUsage";

type ReadTx = Pick<Transaction<Schema>, "run">;
type Exclusion = { exclude: string };
type ConnectorUsageFilter = {
  connectorId?: string;
  spaceId?: string;
  workflowId?: string;
  userId?: string;
} & (
  | { connectorId: string }
  | { spaceId: string }
  | { workflowId: string }
  | { userId: string }
);
type ScopedConnectorUsage = {
  connectorId: string;
  type: ConnectedAppUsageType;
  refId: string;
  subKey: string;
  spaceId?: string;
};
type UsageLookup = (
  tx: ReadTx,
  filter: ConnectorUsageFilter
) => Promise<ScopedConnectorUsage[]>;
type EdgeFilter = { dependentId: string } | { supplierId: string };
type UsageEdge = { dependentId: string; supplierId: string };
type ReactivationTarget = { workflowId: string; commitIds: readonly string[] };
type ConnectorSupplier = { id: string; label: string };
export type ConnectorUsagePolicy = {
  reporting: Record<"list" | "details" | "api", "rows" | Exclusion>;
  reactivation:
    | {
        check: (
          tx: ReadTx,
          target: ReactivationTarget
        ) => Promise<ConnectorSupplier[]>;
      }
    | Exclusion;
  connectorDeletion: { block: string } | { cascade: true };
  personalUse: { space: UsageLookup } | { owner: UsageLookup } | Exclusion;
  membership:
    | { check: (tx: ReadTx, filter: EdgeFilter) => Promise<UsageEdge[]> }
    | { dismiss: UsageLookup }
    | Exclusion;
  accessLoss:
    | { space: UsageLookup }
    | {
        owner: UsageLookup;
        activeOwners: (
          tx: ReadTx,
          tenantId: string,
          connectorId: string
        ) => Promise<Set<string>>;
      }
    | { approval: UsageLookup }
    | Exclusion;
  spaceMove:
    | {
        check: (tx: ReadTx, workflowId: string) => Promise<Iterable<string>>;
        usages: UsageLookup;
      }
    | { dismiss: UsageLookup }
    | Exclusion;
};

function workflowUsages(type: "workflow_slug" | "workflow_chat"): UsageLookup {
  return async (tx, filter) => {
    if (filter.userId) return [];
    let query = zql.connectedAppUsages.where("type", type);
    if (filter.connectorId)
      query = query.where("connectedAppId", filter.connectorId);
    if (filter.workflowId) query = query.where("refId", filter.workflowId);
    if (filter.spaceId)
      query = query.whereExists("workflow", (w) =>
        w.where("spaceId", filter.spaceId!)
      );
    const rows = await tx.run(query.related("workflow"));
    return rows.map(({ connectedAppId: connectorId, ...row }) => ({
      ...row,
      connectorId,
      spaceId: row.workflow?.spaceId,
    }));
  };
}
const workflowSlugUsages = workflowUsages("workflow_slug");
const workflowChatUsages = workflowUsages("workflow_chat");

const stepUsages: UsageLookup = async (tx, filter) => {
  if (filter.userId) return [];
  let query = zql.connectedAppUsages.where("type", "commit_step");
  if (filter.connectorId)
    query = query.where("connectedAppId", filter.connectorId);
  if (filter.workflowId)
    query = query.whereExists("commit", (c) =>
      c.whereExists("branch", (b) => b.where("workflowId", filter.workflowId!))
    );
  if (filter.spaceId)
    query = query.whereExists("commit", (c) =>
      c.whereExists("branch", (b) =>
        b.whereExists("workflow", (w) => w.where("spaceId", filter.spaceId!))
      )
    );
  const rows = await tx.run(
    query.related("commit", (c) =>
      c.related("branch", (b) => b.related("workflow").one()).one()
    )
  );
  return rows.map(({ connectedAppId: connectorId, ...row }) => ({
    ...row,
    connectorId,
    spaceId: row.commit?.branch?.workflow?.spaceId,
  }));
};

const remoteUsages: UsageLookup = async (tx, filter) => {
  if (filter.workflowId || filter.userId) return [];
  let query = zql.connectedAppUsages.where("type", "space_git_remote");
  if (filter.connectorId)
    query = query.where("connectedAppId", filter.connectorId);
  if (filter.spaceId) query = query.where("refId", filter.spaceId);
  const rows = await tx.run(query);
  return rows.map(({ connectedAppId: connectorId, ...row }) => ({
    ...row,
    connectorId,
    spaceId: row.refId,
  }));
};

const personalChatUsages: UsageLookup = async (tx, filter) => {
  if (filter.workflowId || filter.spaceId) return [];
  let query = zql.connectedAppUsages.where("type", "personal_chat");
  if (filter.connectorId)
    query = query.where("connectedAppId", filter.connectorId);
  if (filter.userId)
    query = query.whereExists("personalChat", (c) =>
      c.where("userId", filter.userId!)
    );
  const rows = await tx.run(query);
  return rows.map(({ connectedAppId: connectorId, ...row }) => ({
    ...row,
    connectorId,
  }));
};

function excludeReporting(exclude: string) {
  return { list: { exclude }, details: { exclude }, api: { exclude } };
}

export const CONNECTOR_USAGE_POLICIES = {
  personal_chat: {
    reporting: { list: "rows", details: "rows", api: "rows" },
    reactivation: {
      exclude:
        "Personal chats are independent of workflow recovery and commit restoration.",
    },
    connectorDeletion: { cascade: true },
    personalUse: { owner: personalChatUsages },
    membership: {
      exclude: "Personal chats require their owner’s connector access.",
    },
    accessLoss: {
      owner: personalChatUsages,
      activeOwners: async (tx, _tenantId, connectorId) => {
        // Closed chats stay usable from chat history.
        const rows = await tx.run(
          zql.connectedAppUsages
            .where("type", "personal_chat")
            .where("connectedAppId", connectorId)
            .whereExists("personalChat", (chat) =>
              chat.where("deletedAt", "IS", null)
            )
            .related("personalChat")
        );
        return new Set(
          rows.flatMap((row) =>
            row.personalChat ? [row.personalChat.userId] : []
          )
        );
      },
    },
    spaceMove: { exclude: "Personal chats do not move between spaces." },
  },
  workflow_slug: {
    reporting: excludeReporting("Slug mappings are aliases, not active usage."),
    reactivation: {
      exclude:
        "Slug mappings are aliases; the restored steps establish the dependency.",
    },
    connectorDeletion: { cascade: true },
    personalUse: { space: workflowSlugUsages },
    membership: {
      exclude:
        "Slug mappings can outlive usage; current branch-head steps establish the dependency.",
    },
    accessLoss: { space: workflowSlugUsages },
    spaceMove: {
      check: async (tx, workflowId) =>
        (await workflowSlugUsages(tx, { workflowId })).map(
          (row) => row.connectorId
        ),
      usages: workflowSlugUsages,
    },
  },
  commit_step: {
    reporting: excludeReporting(
      "Workflow usage is reported through current branch-head rollups."
    ),
    reactivation: {
      check: async (tx, { workflowId, commitIds }) => {
        const usages = await tx.run(
          commitStepUsages(zql.connectedAppUsages)
            .where("refId", "IN", commitIds)
            .whereExists("commit", (c) =>
              c.whereExists("branch", (b) => b.where("workflowId", workflowId))
            )
            .related("connectedApp")
        );
        return usages.flatMap(({ connectedApp: connector }) =>
          connector ? [{ id: connector.id, label: connector.name }] : []
        );
      },
    },
    connectorDeletion: { cascade: true },
    personalUse: { space: stepUsages },
    membership: {
      check: async (tx, filter) => {
        if ("dependentId" in filter) {
          const branches = await tx.run(
            liveConnectorUsageRollupsBySpaceQuery([filter.dependentId])
          );
          return [
            ...(liveConnectorIdsBySpace(branches).get(filter.dependentId) ??
              []),
          ].map((supplierId) => ({
            dependentId: filter.dependentId,
            supplierId,
          }));
        }
        const usages = await tx.run(
          liveUsagesOfConnectorQuery(filter.supplierId)
        );
        return usages.flatMap((usage) =>
          usage.branchesAtHead.flatMap((branch) =>
            branch.workflow
              ? [
                  {
                    dependentId: branch.workflow.spaceId,
                    supplierId: filter.supplierId,
                  },
                ]
              : []
          )
        );
      },
    },
    accessLoss: { space: stepUsages },
    spaceMove: {
      check: async (tx, workflowId) => {
        const branches = await tx.run(
          zql.workflowBranches
            .where("workflowId", workflowId)
            .related("headCommit", (c) =>
              c.related("appUsages", commitStepUsages)
            )
        );
        return branches.flatMap(
          (branch) =>
            branch.headCommit?.appUsages.map(
              ({ connectedAppId: connectorId }) => connectorId
            ) ?? []
        );
      },
      usages: stepUsages,
    },
  },
  workflow_chat: {
    reporting: excludeReporting(
      "Build-chat approvals are not displayed in usage reports."
    ),
    reactivation: {
      exclude:
        "Chat approvals belong to the workflow and are not restored from commits.",
    },
    connectorDeletion: { cascade: true },
    personalUse: { space: workflowChatUsages },
    membership: { dismiss: workflowChatUsages },
    accessLoss: { approval: workflowChatUsages },
    spaceMove: { dismiss: workflowChatUsages },
  },
  space_git_remote: {
    reporting: { list: "rows", details: "rows", api: "rows" },
    reactivation: {
      exclude:
        "Space Git remotes are independent of workflow recovery and commit restoration.",
    },
    connectorDeletion: {
      block: "This connector is used for git sync. Disconnect git sync first.",
    },
    personalUse: { space: remoteUsages },
    membership: {
      check: async (tx, filter) =>
        (
          await remoteUsages(
            tx,
            "dependentId" in filter
              ? { spaceId: filter.dependentId }
              : { connectorId: filter.supplierId }
          )
        ).map((row) => ({
          dependentId: row.spaceId!,
          supplierId: row.connectorId,
        })),
    },
    accessLoss: { space: remoteUsages },
    spaceMove: {
      exclude: "Git remotes belong to a space and do not move with workflows.",
    },
  },
  skill_git_source: {
    reporting: {
      list: {
        exclude:
          "Skill sources are shown in connector details, not list badges.",
      },
      details: "rows",
      api: {
        exclude: "The public API usage response has no skill source field.",
      },
    },
    reactivation: {
      exclude:
        "Skill Git sources are independent of workflow recovery and commit restoration.",
    },
    connectorDeletion: {
      block:
        "This connector is used by an installed skill. Remove the skill source first.",
    },
    personalUse: {
      exclude: "Skill Git sources belong to the tenant, not personal work.",
    },
    membership: {
      exclude: "Skill Git sources belong to the tenant, not a space.",
    },
    accessLoss: {
      exclude:
        "Skill management, rather than a principal’s connector grant, controls the Git source.",
    },
    spaceMove: { exclude: "Skill Git sources do not move with workflows." },
  },
  audit_log_destination: {
    reporting: { list: "rows", details: "rows", api: "rows" },
    reactivation: {
      exclude:
        "Audit log destinations are independent of workflow recovery and commit restoration.",
    },
    connectorDeletion: {
      block:
        "This connector is used by an audit log destination. Remove it from the destination first.",
    },
    personalUse: {
      exclude:
        "Audit log destinations belong to the tenant, not personal work.",
    },
    membership: {
      exclude: "Audit log destinations belong to the tenant, not a space.",
    },
    accessLoss: {
      exclude:
        "Audit delivery uses a tenant configuration independently of the configuring principal’s grants.",
    },
    spaceMove: {
      exclude: "Audit log destinations do not move with workflows.",
    },
  },
  ai_provider: {
    reporting: {
      list: "rows",
      details: "rows",
      api: {
        exclude: "The public API usage response has no AI provider field.",
      },
    },
    reactivation: {
      exclude:
        "AI providers are independent of workflow recovery and commit restoration.",
    },
    connectorDeletion: {
      block:
        "This connector is used by an AI provider. In AI provider settings, switch that provider to another connector or delete the provider first.",
    },
    personalUse: {
      exclude: "AI providers belong to the tenant, not personal work.",
    },
    membership: {
      exclude:
        "AI providers have separate connector access rules, outside space membership.",
    },
    accessLoss: {
      exclude:
        "AI provider connector associations are governed by provider configuration, not space or owner membership.",
    },
    spaceMove: { exclude: "AI providers do not move with workflows." },
  },
} satisfies Record<ConnectedAppUsageType, ConnectorUsagePolicy>;

export const connectorDeletionBlockers = (
  Object.keys(CONNECTOR_USAGE_POLICIES) as ConnectedAppUsageType[]
).flatMap((type) => {
  const policy: ConnectorUsagePolicy = CONNECTOR_USAGE_POLICIES[type];
  return "block" in policy.connectorDeletion
    ? [{ type, message: policy.connectorDeletion.block }]
    : [];
});

export type ConnectorMembershipUsageType = {
  [
    T in ConnectedAppUsageType
  ]: "check" extends keyof (typeof CONNECTOR_USAGE_POLICIES)[T]["membership"]
    ? T
    : never;
}[ConnectedAppUsageType];

export const connectorUsagePolicies: readonly ConnectorUsagePolicy[] =
  Object.values(CONNECTOR_USAGE_POLICIES);

export async function connectorSuppliersForReactivation(
  tx: ReadTx,
  target: ReactivationTarget
): Promise<ConnectorSupplier[]> {
  const suppliers = await Promise.all(
    connectorUsagePolicies.flatMap(({ reactivation }) =>
      "check" in reactivation ? [reactivation.check(tx, target)] : []
    )
  );
  return [
    ...new Map(
      suppliers.flat().map((supplier) => [supplier.id, supplier])
    ).values(),
  ];
}

export async function spaceConnectorUsageEdges(
  tx: ReadTx,
  filter: EdgeFilter
): Promise<UsageEdge[]> {
  const edges = await Promise.all(
    connectorUsagePolicies.flatMap(({ membership }) =>
      "check" in membership ? [membership.check(tx, filter)] : []
    )
  );
  return edges.flat();
}

export async function connectorUsageOwnerIds(
  tx: ReadTx,
  tenantId: string,
  connectorId: string
): Promise<Set<string>> {
  const users = await Promise.all(
    connectorUsagePolicies.flatMap(({ accessLoss }) =>
      "owner" in accessLoss
        ? [accessLoss.activeOwners(tx, tenantId, connectorId)]
        : []
    )
  );
  return new Set(users.flatMap((ids) => [...ids]));
}
